import { downloadScientificProfileFile } from 'apis/projectProposals.api';
import './style.scss';

import { downloadAvatarById } from 'apis/scientificProfiles.api';
import { Avatar } from 'primereact/avatar';
import { Card } from 'primereact/card';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { convertDataToUrl } from 'utils/func';

function ScientistCard({
  data,
  formatFullname,
  formatEmail,
  formatOrganization,
  formatResearchField,
}) {
  const { t } = useTranslation();

  const [imagePreview, setImagePreview] = useState(null);

  const idProfile = data?.contacts?.[0]?.scientificProfile?.publicProfile?.publicProfileId;

  const fileUrl = useMemo(() => data?.scientificProfileUrl ?? null, [data]);

  const scientificProfile = useMemo(() => data?.contacts?.[0]?.scientificProfile, [data]);

  const { mutate: mutateDownloadScientificProfileFile, isLoading: isLoadingDownload } = useMutation(
    downloadScientificProfileFile
  );

  const handleDisplayScientificProfileFile = () => {
    mutateDownloadScientificProfileFile(data?.contacts?.[0]?.id, {
      onSuccess: async (res) => {
        if (res?.data) {
          const file = new Blob([res.data], { type: 'application/pdf' });
          const blobURL = URL.createObjectURL(file);
          window.open(blobURL);
        }
      },
    });
  };

  useQuery(['avatar', idProfile], () => downloadAvatarById(idProfile), {
    // Modify this line
    enabled: imagePreview === null && !!idProfile && !!data?.avatarUrl,
    onSuccess: async (res) => {
      if (res?.data) {
        const imageBlob = new Blob([res.data]);

        const imageUrl = await convertDataToUrl(imageBlob);
        setImagePreview(imageUrl);
      }
    },
    onError: () => {
      setImagePreview(
        'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'
      );
    },
  });

  return (
    <Card title={formatFullname(data)} className="scientist-card">
      <div className="flex gap-3">
        <Avatar
          image={
            imagePreview ||
            'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png'
          }
          style={{ width: '80px', height: '80px' }}
          shape="circle"
          className="flex-shrink-0"
        />
        <div className="flex flex-column">
          <p>
            <i className="pi pi-home" />
            {formatOrganization(data)}
          </p>
          {scientificProfile?.faculty && (
            <p>
              <i className="pi pi-briefcase" />
              {scientificProfile?.faculty}
            </p>
          )}
          {data?.userResearchFields?.length > 0 && (
            <p>
              <i className="pi pi-tags" />
              {formatResearchField(data)}
            </p>
          )}
          {scientificProfile?.officeAddress && (
            <p>
              <i className="pi pi-map-marker" />
              {scientificProfile?.officeAddress}
            </p>
          )}
          <p>
            <i className="pi pi-envelope" />
            {formatEmail(data?.email)}
          </p>
          {fileUrl && (
            <div className="">
              <span
                className="p-button p-button-text p-button-sm p-0 mt-1"
                onClick={() => handleDisplayScientificProfileFile()}
              >
                {isLoadingDownload ? (
                  <i className="pi pi-spin pi-spinner" />
                ) : (
                  <>
                    <i className="pi pi-paperclip" />
                    <span className="text-blue-500 underline font-italic text-base ml-1">
                      {t('scientificProfile.viewFile')}
                    </span>
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

ScientistCard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    avatarUrl: PropTypes.string,
    contacts: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        fullname: PropTypes.string.isRequired,
        scientificProfile: PropTypes.shape({
          publicProfile: PropTypes.shape({
            id: PropTypes.string,
            publicProfileId: PropTypes.string,
          }),
          academicRank: PropTypes.string,
          academicDegree: PropTypes.string,
          gender: PropTypes.string,
        }).isRequired,
      }).isRequired
    ).isRequired,
    fullname: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    scientificProfileUrl: PropTypes.string,
    userRoles: PropTypes.arrayOf(
      PropTypes.shape({
        role: PropTypes.shape({
          code: PropTypes.string.isRequired,
        }).isRequired,
      })
    ),
    userResearchFields: PropTypes.arrayOf(
      PropTypes.shape({
        researchField: PropTypes.shape({
          name: PropTypes.string.isRequired,
        }).isRequired,
      })
    ),
  }).isRequired,
  formatFullname: PropTypes.func.isRequired,
  formatEmail: PropTypes.func.isRequired,
  formatOrganization: PropTypes.func.isRequired,
  formatResearchField: PropTypes.func.isRequired,
};

export default React.memo(ScientistCard);
